import KomatsuInputLabel from '../KomatsuInputLabel'
import { Error as ErrorIcon } from '@mui/icons-material'
import { Box, FormHelperText, Input, InputProps, Popover, Typography } from '@mui/material'
import React, { forwardRef, useEffect, useRef, useState } from 'react'

interface KomatsuInputProps extends InputProps {
  label?: string
  name: string
  endAdornment?: React.ReactNode
  helperText?: string
  error?: boolean
  errorMessage?: string | React.ReactNode
  tooltip?: React.ReactNode
}

const KomatsuInput = forwardRef<HTMLInputElement, KomatsuInputProps>(
  (
    {
      label,
      placeholder,
      type,
      name,
      helperText,
      fullWidth = true,
      required,
      error = false,
      errorMessage,
      value,
      onChange,
      onFocus,
      onBlur,
      endAdornment,
      tooltip,
      sx,
      ...props
    },
    ref
  ) => {
    const [errorPopoverAnchor, setErrorPopoverAnchor] = useState<HTMLElement | null>(null)
    const [isFocused, setIsFocused] = useState(false)
    const inputRootRef = useRef<HTMLDivElement | null>(null)

    // Auto-open error popover when error appears and input is focused
    useEffect(() => {
      if (error && errorMessage && isFocused && inputRootRef.current) {
        setErrorPopoverAnchor(inputRootRef.current)
      } else {
        setErrorPopoverAnchor(null)
      }
    }, [error, errorMessage, isFocused])

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true)
      if (onFocus) {
        onFocus(e)
      }
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false)
      if (onBlur) {
        onBlur(e)
      }
    }

    // Merge endAdornment with error icon if error exists
    const hasErrorIcon = error && errorMessage
    const mergedEndAdornment =
      hasErrorIcon || endAdornment ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {hasErrorIcon && (
            <ErrorIcon
              sx={{
                fontSize: '16px',
                color: 'error.main',
                cursor: 'default',
                animation: 'fadeInScale 0.3s ease-out',
                '@keyframes fadeInScale': {
                  '0%': {
                    opacity: 0,
                    transform: 'scale(0.5)'
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'scale(1)'
                  }
                }
              }}
            />
          )}
          {endAdornment}
        </Box>
      ) : undefined

    return (
      <Box hidden={type === 'hidden'}>
        {label && (
          <KomatsuInputLabel
            error={error}
            name={name}
            label={label}
            required={required}
            tooltip={tooltip}
          />
        )}
        <Box ref={inputRootRef}>
          <Input
            {...props}
            ref={ref}
            id={name}
            name={name}
            type={type}
            placeholder={placeholder}
            fullWidth={fullWidth}
            required={required}
            value={value}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disableUnderline
            error={error}
            endAdornment={mergedEndAdornment}
            sx={{
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
              ...(error && {
                bgcolor: 'rgba(211, 47, 47, 0.04)', // Very subtle washed-out red/pink
                '&:hover': {
                  bgcolor: 'rgba(211, 47, 47, 0.06)'
                },
                '&.Mui-focused': {
                  bgcolor: 'rgba(211, 47, 47, 0.08)'
                }
              }),
              ...sx
            }}
          />
        </Box>

        {helperText ? (
          <FormHelperText sx={{ margin: '3px 14px 0 0px' }}>{helperText}</FormHelperText>
        ) : null}
        {/* {(error && errorMessage) || helperText ? (
        <FormHelperText error={error} sx={{ margin: '3px 14px 0 0px' }}>
          {error && errorMessage ? errorMessage : helperText}
        </FormHelperText>
      ) : null} */}

        {/* Error Popover */}
        {errorPopoverAnchor && errorMessage && (
          <Popover
            open={true}
            anchorEl={errorPopoverAnchor}
            onClose={() => setErrorPopoverAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center'
            }}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            sx={{
              pointerEvents: 'none',
              '& .MuiPaper-root': {
                maxWidth: 400,
                p: 1.5,
                pointerEvents: 'auto',
                bgcolor: 'rgba(255, 245, 245, 0.98)',
                boxShadow: '0 2px 8px rgba(211, 47, 47, 0.2)',
                marginTop: 0,
                userSelect: 'text',
                WebkitUserSelect: 'text',
                MozUserSelect: 'text',
                msUserSelect: 'text',
                animation: 'popoverFadeIn 0.2s ease-out',
                '@keyframes popoverFadeIn': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(-8px) scale(0.95)'
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0) scale(1)'
                  }
                }
              }
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                  MozUserSelect: 'text',
                  msUserSelect: 'text'
                }}
              >
                {errorMessage}
              </Typography>
            </Box>
          </Popover>
        )}
      </Box>
    )
  }
)

KomatsuInput.displayName = 'KomatsuInput'

export default KomatsuInput
